/** Attention Home page (UX spec §3.8, decision D-3): the landing surface is
 *  a work queue. One card per non-zero count, each deep-linking to the page
 *  where the items can be acted on; an all-clear state otherwise. */

import { Link } from 'react-router-dom'
import { appPath } from '../../appConfig.ts'
import { useAttentionSummary } from '../../hooks/useAttention.ts'
import { useAuth } from '../../hooks/useAuth.ts'
// Spec §3.12: capability checks go through hasCapability. Today it lives in
// the gis-queue types module; the Stage-2 C5 task relocates it to
// utils/capabilities.ts with a re-export here, so this import stays valid.
import { hasCapability } from '../gis-queue/types.ts'
import { buildAttentionCards, type AttentionCard } from './attentionCards.ts'

export function AttentionPage() {
  const { user } = useAuth()
  const { data, isLoading, isError } = useAttentionSummary()
  const canSeeGisQueue = hasCapability(user?.capabilities, 'gis.queue.view')

  return (
    <div className="flex-1 overflow-auto bg-gray-50">
      <div className="max-w-5xl mx-auto p-4 space-y-4">
        <div className="flex items-end justify-between gap-4 flex-wrap">
          <div>
            <h2 className="text-lg font-semibold text-gray-800">Home</h2>
            <p className="text-sm text-gray-500">
              What needs attention right now. Each card opens the page where
              you can act on it.
            </p>
          </div>
          {data && (
            <div className="text-xs text-gray-400">
              Checked {new Date(data.as_of).toLocaleString()}
            </div>
          )}
        </div>

        {isLoading && (
          <div className="text-sm text-gray-500">Checking the system…</div>
        )}

        {isError && (
          <div className="bg-red-50 border border-red-200 rounded-lg p-4 text-sm text-red-700">
            Couldn't load the summary. Try again; if it keeps failing, tell the
            engineer.
          </div>
        )}

        {data && (
          <CardGrid cards={buildAttentionCards(data, canSeeGisQueue)} />
        )}
      </div>
    </div>
  )
}

function CardGrid({ cards }: { cards: AttentionCard[] }) {
  if (cards.length === 0) {
    return (
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 text-center space-y-2">
        <div className="text-base font-medium text-gray-800">
          Nothing needs attention right now
        </div>
        <div className="text-sm text-gray-500">
          No active outages, no overloads, nothing waiting for review.
        </div>
        <Link
          to={appPath('/map')}
          className="inline-block text-sm text-blue-600 hover:underline"
        >
          Open the map
        </Link>
      </div>
    )
  }
  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
      {cards.map((card) => (
        <Link
          key={card.key}
          to={appPath(card.to)}
          className="block bg-white rounded-lg shadow-sm border border-gray-200 p-4 hover:border-blue-300 hover:shadow transition-colors space-y-1"
        >
          <div className="text-base font-semibold text-gray-800">
            {card.headline}
          </div>
          <div className="text-sm text-gray-500">{card.detail}</div>
          <div className="text-sm text-blue-600">{card.linkLabel} →</div>
        </Link>
      ))}
    </div>
  )
}
